// Decorative SVG ornaments — ported from legacy-mockup/cards.jsx.
// Each is a tiny pure SVG component, used as accents in cards and variants.

interface OrnamentProps {
  size?: number;
  width?: number;
  color?: string;
  accent?: string;
}

export const Diamond = ({ size = 16, color = "currentColor" }: OrnamentProps) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
    <path d="M8 1 L11 8 L8 15 L5 8 Z" stroke={color} strokeWidth="0.7" />
    <circle cx="8" cy="8" r="1" fill={color} />
  </svg>
);

export const Star = ({ size = 14, color = "currentColor" }: OrnamentProps) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden>
    <path d="M7 0 L8 6 L14 7 L8 8 L7 14 L6 8 L0 7 L6 6 Z" fill={color} />
  </svg>
);

export const Flourish = ({ width = 80, color = "currentColor" }: OrnamentProps) => (
  <svg width={width} height={width * 0.18} viewBox="0 0 80 14" fill="none" aria-hidden>
    <path d="M0 7 Q 20 7 30 4 T 40 7 T 50 10 T 60 7 Q 70 7 80 7" stroke={color} strokeWidth="0.6" fill="none" />
    <circle cx="40" cy="7" r="1.5" fill={color} />
    <circle cx="20" cy="7" r="0.8" fill={color} />
    <circle cx="60" cy="7" r="0.8" fill={color} />
  </svg>
);

export const Wreath = ({ size = 80, color = "currentColor" }: OrnamentProps) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" aria-hidden>
    <circle cx="40" cy="40" r="32" stroke={color} strokeWidth="0.5" opacity="0.4" />
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i * 30 * Math.PI) / 180;
      const x1 = 40 + Math.cos(a) * 32;
      const y1 = 40 + Math.sin(a) * 32;
      const x2 = 40 + Math.cos(a) * 38;
      const y2 = 40 + Math.sin(a) * 38;
      return (
        <g key={i}>
          <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="0.5" />
          <circle cx={x2} cy={y2} r="1" fill={color} />
        </g>
      );
    })}
  </svg>
);

export const Suzani = ({ size = 60, color = "currentColor" }: OrnamentProps) => (
  <svg width={size} height={size} viewBox="0 0 60 60" fill="none" aria-hidden>
    <g stroke={color} strokeWidth="0.6" fill="none">
      <circle cx="30" cy="30" r="4" />
      <circle cx="30" cy="30" r="10" />
      <circle cx="30" cy="30" r="18" />
      {[0, 60, 120, 180, 240, 300].map((deg) => (
        <g key={deg} transform={`rotate(${deg} 30 30)`}>
          <ellipse cx="30" cy="14" rx="2" ry="5" fill={color} fillOpacity="0.4" />
          <line x1="30" y1="20" x2="30" y2="26" />
        </g>
      ))}
    </g>
    <circle cx="30" cy="30" r="2" fill={color} />
  </svg>
);

export const DecoCorner = ({ size = 60, color = "currentColor" }: OrnamentProps) => (
  <svg width={size} height={size} viewBox="0 0 60 60" fill="none" aria-hidden>
    <g stroke={color} strokeWidth="0.7" fill="none">
      <path d="M0 0 L 60 0 L 60 4 L 4 4 L 4 60 L 0 60 Z" fill={color} stroke="none" />
      <path d="M8 8 L 50 8 M 8 8 L 8 50" />
      <path d="M14 14 L 36 14 M 14 14 L 14 36" />
      <circle cx="20" cy="20" r="2" fill={color} />
    </g>
  </svg>
);

export const Vine = ({ width = 200, color = "currentColor" }: OrnamentProps) => (
  <svg width={width} height={width * 0.15} viewBox="0 0 200 30" fill="none" aria-hidden>
    <path d="M0 15 Q 25 5 50 15 T 100 15 T 150 15 T 200 15" stroke={color} strokeWidth="0.7" fill="none" />
    {[25, 75, 125, 175].map((cx, i) => (
      <g key={i} transform={`translate(${cx}, 15)`}>
        <ellipse cx="0" cy="-6" rx="3" ry="5" fill={color} fillOpacity="0.5" transform={`rotate(${i % 2 ? -30 : 30})`} />
        <ellipse cx="0" cy="6" rx="2" ry="4" fill={color} fillOpacity="0.3" transform={`rotate(${i % 2 ? 30 : -30})`} />
      </g>
    ))}
  </svg>
);

export const IkatPattern = ({ size = 200, color = "currentColor", accent = "currentColor" }: OrnamentProps) => (
  <svg width={size} height={size} viewBox="0 0 200 200" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden>
    <defs>
      <pattern id="ikat" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse">
        <g opacity="0.55">
          <path d="M25 5 L35 15 L45 5 L45 20 L25 25 L5 20 L5 5 L15 15 Z" fill={accent} />
          <circle cx="25" cy="35" r="6" fill={color} />
          <path d="M10 40 L15 45 L20 40 M30 40 L35 45 L40 40" stroke={color} strokeWidth="1.5" fill="none" />
        </g>
      </pattern>
    </defs>
    <rect width="200" height="200" fill="url(#ikat)" />
  </svg>
);
